import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import { SectionList } from '@/features/storefront/sections/SectionRenderer';
import type { AnchorSectionType, Section } from '@/features/storefront/sections/schema';
import { useSectionData } from '@/features/storefront/sections/useSectionData';

/**
 * A shopper-side page, rendered from the sections the merchant arranged.
 *
 * Every customisable page has the same four things to say to `SectionList` -
 * the store's name, its catalog, that this is not a preview, and where a
 * product card points - and none of them differ by page. Written once here so
 * that adding a page is a component that computes its own content and hands it
 * over as a slot, rather than a seventh copy of this wiring.
 *
 * The catalog it fetches is the one the *sections* ask for, not the one the
 * page needs: a product page whose merchant put a "you may also like" grid
 * under the buy button needs those products, and a product page whose merchant
 * did not needs no extra request at all.
 */
export interface StorefrontPageSectionsProps {
  sections: Section[];
  /** The page's own content, by the anchor that stands for it. */
  slots?: Partial<Record<AnchorSectionType, ReactNode>>;
  /**
   * Where a product card in a section points. Defaults to the address the
   * home page's sections have always written.
   */
  productHref?: (slug: string) => string;
  /** The product the page is about, for the sections that need one - reviews. */
  product?: { slug: string; name: string } | null;
}

export function StorefrontPageSections({
  sections,
  slots,
  productHref = (slug) => `/products/${slug}`,
  product,
}: StorefrontPageSectionsProps) {
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const context = useQuery({ queryKey: [...scope, 'context'], queryFn: () => source.context() });
  const data = useSectionData(sections);

  return (
    <SectionList
      sections={sections}
      storeName={context.data?.store_name ?? ''}
      preview={false}
      productHref={productHref}
      slots={slots}
      product={product}
      {...data}
    />
  );
}
